import React, { forwardRef } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "danger"
    | "success";
    size?: "sm" | "md" | "lg";
    loading?: boolean;
    fullWidth?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = "primary",
            size = "md",
            loading = false,
            disabled,
            fullWidth = false,
            leftIcon,
            rightIcon,
            className,
            ...props
        },
        ref
    ) => {
        const baseStyles =
            "inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:opacity-60";

        const variants = {
            primary:
                "bg-white text-slate-900 hover:bg-slate-100 focus:ring-white/30 shadow-lg",

            secondary:
                "bg-slate-100 text-slate-800 hover:bg-slate-200 focus:ring-slate-200",

            outline:
                "border border-violet-600 text-violet-600 hover:bg-violet-50 focus:ring-violet-200",

            ghost:
                "text-slate-700 hover:bg-slate-100 focus:ring-slate-200",

            danger:
                "bg-red-600 text-white hover:bg-red-700 focus:ring-red-200",

            success:
                "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-200",
        };

        const sizes = {
            sm: "h-10 px-4 text-sm",
            md: "h-12 px-6 text-base",
            lg: "h-14 px-8 text-lg",
        };

        return (
            <button
                ref={ref}
                disabled={disabled || loading}
                className={clsx(
                    baseStyles,
                    variants[variant],
                    sizes[size],
                    fullWidth && "w-full",
                    className,
                    "outline-none focus:ring-none"
                )}
                {...props}
            >
                {loading ? (
                    <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Loading...
                    </>
                ) : (
                    <>
                        {leftIcon && <span className="mr-2">{leftIcon}</span>}

                        {children}

                        {rightIcon && <span className="ml-2">{rightIcon}</span>}
                    </>
                )}
            </button>
        );
    }
);

Button.displayName = "Button";

export default Button;